<template>
  <form @submit.prevent="addSupplier">
    <div class="row">
      <!-- Supplier ID -->
      <div class="mb-3">
        <label class="form-label">Supplier ID</label>
        <input
          v-model="form.supplierid"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': errors.supplierid }"
          readonly
        >
        <div v-if="errors.supplierid" class="text-danger">
          {{ errors.supplierid[0] }}
        </div>
      </div>

      <!-- Supplier Name -->
      <div class="mb-3">
        <label class="form-label">Supplier Name</label>
        <input
          v-model="form.name"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': errors.name }"
          placeholder="Enter supplier name"
        >
        <div v-if="errors.name" class="text-danger">
          {{ errors.name[0] }}
        </div>
      </div>

      <!-- Phone -->
      <div class="input-blocks">
        <label class="mb-2">Phone</label>
        <input
          v-model="form.phone"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': errors.phone }"
          placeholder="01XXXXXXXXX"
          maxlength="11"
        >
        <div v-if="errors.phone" class="text-danger">
          {{ errors.phone[0] }}
        </div>
      </div>

      <!-- Address -->
      <div class="mb-3">
        <label class="form-label">Address</label>
        <input
          v-model="form.address"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': errors.address }"
          placeholder="Enter supplier address"
        >
        <div v-if="errors.address" class="text-danger">
          {{ errors.address[0] }}
        </div>
      </div>
    </div>

    <!-- Modal Footer -->
    <div class="modal-footer-btn">
      <button
        type="button"
        class="btn btn-cancel me-2"
        @click="cancelForm"
      >
        Cancel
      </button>
      <button
        type="submit"
        class="btn btn-submit"
        :disabled="loading"
      >
        {{ loading ? 'Creating...' : 'Submit' }}
      </button>
    </div>
  </form>
</template>

<script>
import { showSuccessMessage, showErrorMessage } from '../utils/notifications.js';

export default {
  name: 'SupplierCreate',
  props: {
    initialSupplierId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      form: {
        supplierid: this.initialSupplierId,
        name: '',
        phone: '',
        address: ''
      },
      errors: {},
      loading: false
    }
  },
  methods: {
    async addSupplier() {
      this.loading = true;
      this.errors = {};

      try {
        console.log('Submitting supplier form:', this.form);

        const response = await this.$http.post('/api/suppliers', this.form);

        // Handle success
        this.showSuccessMessage('Supplier added successfully!');
        this.resetForm();

        // Emit events for parent components
        this.$emit('supplier-added', response.data);

        // Dispatch custom event for modal handling
        document.dispatchEvent(new CustomEvent('supplier-created', {
          detail: response.data
        }));

        // Close modal if in modal context
        this.closeModal();

      } catch (error) {
        console.error('Supplier creation error:', error);

        if (error.response) {
          console.error('Error response:', error.response.data);
          console.error('Error status:', error.response.status);

          if (error.response.status === 422) {
            // Validation errors
            this.errors = error.response.data.errors;
          } else if (error.response.status === 401) {
            // Authentication error
            this.showErrorMessage('Authentication failed. Please refresh the page and try again.');
          } else {
            // Other errors
            this.showErrorMessage(error.response.data.message || 'An error occurred while creating the supplier.');
          }
        } else {
          // Network or other errors
          this.showErrorMessage('Network error. Please check your connection and try again.');
          console.error('Network error:', error.message);
        }
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.form = {
        supplierid: this.initialSupplierId,
        name: '',
        phone: '',
        address: ''
      };
      this.errors = {};
    },

    cancelForm() {
      this.resetForm();
      this.$emit('cancel');
      this.closeModal();
    },

    closeModal() {
      // If in modal, close it
      if (window.bootstrap && window.bootstrap.Modal) {
        const modal = window.bootstrap.Modal.getInstance(this.$el.closest('.modal'));
        if (modal) {
          modal.hide();
        }
      }
    },

    async generateSupplierId() {
      try {
        const response = await this.$http.get('/api/suppliers/generate-id');
        this.form.supplierid = response.data.supplierid;
      } catch (error) {
        console.error('Failed to generate supplier ID:', error);
        // Fallback to timestamp-based ID
        this.form.supplierid = 'SUPP' + Date.now();
      }
    },

    showSuccessMessage,
    showErrorMessage
  },

  async mounted() {
    console.log('SupplierCreate component mounted');
    console.log('Initial Supplier ID:', this.initialSupplierId);

    // Generate supplier ID if not provided
    if (!this.form.supplierid) {
      await this.generateSupplierId();
    }

    // Mark element as mounted for loading state
    this.$el.closest('[data-vue-component]')?.classList.add('vue-mounted');
  }
}
</script>

<style scoped>
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.text-danger {
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.form-control.is-invalid {
  border-color: #dc3545;
}

.input-blocks {
  margin-bottom: 1rem;
}

.input-blocks label {
  font-weight: 600;
}
</style>
